
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotificationMutations } from "@/hooks/useNotificationMutations";
import NotificationsViewTab from "@/components/notifications/NotificationsViewTab";
import NotificationsCreateTab from "@/components/notifications/NotificationsCreateTab";
import { usePermissions } from "@/context/PermissionsContext";
import { Eye, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchParams } from "react-router-dom";
import { useStockMonitoringRealtime } from "@/hooks/notifications/useStockMonitoringRealtime";
import { useChatStatusRealtime } from "@/hooks/notifications/useChatStatusRealtime";
import "../styles/mobile-notifications.css";

const Notifications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Migration: move old 'view=view|create' to 'tab' parameter
  useEffect(() => {
    const view = searchParams.get("view");
    if (view === "view" || view === "create") {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", view);
      newParams.set("view", "active");
      setSearchParams(newParams, { replace: true });
    }
  }, []);
  
  const initialActiveTab = searchParams.get("tab") === "create" ? "create" : "view";
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  
  // Keep activeTab in sync with URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const newTab = tabParam === "create" ? "create" : "view";
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);
  const { archiveNotification, restoreNotification, deleteNotification } = useNotificationMutations(undefined);
  const { hasPermission, isLoading } = usePermissions();
  const [canCreateNotifications, setCanCreateNotifications] = useState(false);
  const isMobile = useIsMobile();

  // Set up real-time subscriptions for stock and chat
  useStockMonitoringRealtime();
  useChatStatusRealtime();

  // Add a class to the body when on mobile to help with CSS targeting
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('has-mobile-notification');
    } else {
      document.body.classList.remove('has-mobile-notification');
    }
    
    return () => {
      document.body.classList.remove('has-mobile-notification');
      document.body.classList.remove('form-submitting');
      document.body.classList.remove('form-fixed');
    };
  }, [isMobile]);

  // Check permissions on component mount
  useEffect(() => {
    const canWrite = hasPermission("notifications", "write");
    console.log("Notifications - check write permission:", canWrite);
    setCanCreateNotifications(canWrite);
  }, [hasPermission, isLoading]);

  const handleArchive = (id: string) => {
    if (!hasPermission("notifications", "write")) {
      toast.error("Je hebt geen rechten om meldingen te archiveren");
      return;
    }
    archiveNotification.mutate(id);
  };

  const handleRestore = (id: string) => {
    if (!hasPermission("notifications", "write")) {
      toast.error("Je hebt geen rechten om meldingen te herstellen");
      return;
    }
    restoreNotification.mutate(id);
  };

  const handleDelete = (id: string) => {
    if (!hasPermission("notifications", "delete")) {
      toast.error("Je hebt geen rechten om meldingen te verwijderen");
      return;
    }
    deleteNotification.mutate(id);
  };

  // Cancel handler that ensures we remove any fixed classes
  const handleCancel = () => {
    if (isMobile) {
      document.body.classList.remove('form-submitting');
      document.body.classList.remove('form-fixed');
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", "view");
    setSearchParams(newParams, { replace: true });
  };
  
  // Handle tab changes
  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div 
      className={`space-y-4 ${isMobile ? 'pb-16 mobile-notifications-container' : 'space-y-6'}`} 
      style={isMobile ? { 
        touchAction: 'auto', 
        position: 'static',
        transform: 'none',
        pointerEvents: 'auto'
      } : undefined}
    >
      <div>
        <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>Meldingen</h1>
        {!isMobile && (
          <p className="text-muted-foreground">
            Bekijk en verstuur meldingen binnen het systeem
          </p>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className={isMobile ? 'mt-2' : ''}>
        <TabsList className={`grid w-full ${canCreateNotifications ? 'grid-cols-2' : 'grid-cols-1'} ${isMobile ? 'sticky top-0 z-10 mb-3 bg-background rounded-lg' : ''}`}>
          <TabsTrigger 
            value="view" 
            className={`bg-luijten-red text-white hover:bg-luijten-red/90 transition-colors ${isMobile ? 'py-3' : ''}`}
          >
            <Eye className="mr-2 h-4 w-4" />
            {isMobile ? "Bekijk" : "Bekijken"}
          </TabsTrigger>
          {canCreateNotifications && (
            <TabsTrigger 
              value="create" 
              className={`bg-luijten-red text-white hover:bg-luijten-red/90 transition-colors ${isMobile ? 'py-3' : ''}`}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              {isMobile ? "Nieuw" : "Nieuwe melding"}
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="view" className={isMobile ? 'mt-0 fixed-mobile-card' : ''}>
          <NotificationsViewTab
            onArchiveNotification={handleArchive}
            onRestoreNotification={handleRestore}
            onDeleteNotification={handleDelete}
          />
        </TabsContent>
        
        {canCreateNotifications && (
          <TabsContent value="create" className={isMobile ? 'mt-0 fixed-mobile-card' : ''}>
            <NotificationsCreateTab
              onCancel={handleCancel}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Notifications;
